import { EntityType } from '@frontend/common';
import { SettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';

export const DEFAULT_PIN_LENGTH = 4;

let cachedPinLength: number | null = null;
let fetchPromise: Promise<number> | null = null;

/**
 * Fetches and caches the PIN-code length from settings.
 * Uses in-memory cache to avoid repeated API calls.
 * Cache is cleared on app refresh.
 */
export async function getPinCodeLength(accountId: string): Promise<number> {
    // Return cached value if available
    if (cachedPinLength !== null) {
        return cachedPinLength;
    }

    // If a fetch is already in progress, return that promise
    if (fetchPromise !== null) {
        return fetchPromise;
    }

    // Start new fetch
    fetchPromise = (async () => {
        try {
            const response = await SettingClient.fetchSettings({
                entity_type: EntityType.ACCOUNT,
                entity_id: accountId,
                key: SettingKey.PIN_CODE_LENGTH
            });

            if (response.count === 0 || response.results.length === 0 || !response.results[0]) {
                cachedPinLength = DEFAULT_PIN_LENGTH;
                return DEFAULT_PIN_LENGTH;
            }

            const setting = response.results[0];
            const length = Number(setting.value);

            if (isNaN(length) || length <= 0) {
                cachedPinLength = DEFAULT_PIN_LENGTH;
                return DEFAULT_PIN_LENGTH;
            }

            cachedPinLength = length;
            return length;
        } catch (error) {
            console.error('Failed to fetch PIN-code length setting:', error);
            cachedPinLength = DEFAULT_PIN_LENGTH;
            return DEFAULT_PIN_LENGTH;
        } finally {
            fetchPromise = null;
        }
    })();

    return fetchPromise;
}

/**
 * Clears the cached PIN-code length.
 * Useful for testing or manual cache invalidation.
 */
export function clearPinCodeCache(): void {
    cachedPinLength = null;
    fetchPromise = null;
}
